/**
 * The sound of the prize wheel.
 *
 * The ticks are a recording (roulette-tick.mp3). The rest is synthesised,
 * like the mine's (mine.ts), and built the way the real object makes its
 * noise rather than from stock beeps:
 *
 *   - **The release.** A wheel set going is a low thump of the hub taking the
 *     shove and a short latch click. A sine falling 95 → 55 Hz under a burst
 *     of band-passed noise.
 *   - **The ticks.** The pointer's flapper striking each peg: the recorded
 *     tick, each one nudged a little in pitch and level so a hundred of them
 *     never sound like one file repeating. They are placed exactly where the
 *     disc's pegs pass the pointer, from the same curve the disc turns on, so
 *     they slow down with it. Until the file is decoded, or if it cannot be,
 *     a synthesised tick stands in: a hard transient near 3 kHz, the
 *     flapper's inharmonic ring around 1.2 kHz and a knock of the peg's wood.
 *   - **The stop.** A soft, damped clunk as the disc comes to rest.
 *   - **The prize.** Struck metal bars — the partial ratios of a real
 *     glockenspiel bar (1, 2.76, 5.40, 8.93), one clean voice each — rising
 *     in a major arpeggio, more notes for a bigger prize.
 *     Nothing won is two low wooden notes falling, quiet: news, not mockery.
 *
 * Dry, on purpose: no reverb and no long rings. A wheel in a game is close to
 * the ear, and a tail of echo blurs every tick into the next. Everything goes
 * through a gentle compressor only, which glues the parts of each sound
 * together without smearing them.
 *
 * Like the mine: nothing starts without a gesture (the spin button is one),
 * and nothing is heard unless the user turned sound on.
 */

import tickUrl from './roulette-tick.mp3';

export type RoulettePrizeWeight = 'none' | 'small' | 'big';

export interface RouletteSpinPlan {
  /** How long the disc turns, in milliseconds. */
  durationMs: number;
  /** The disc's angle before and after, in degrees. */
  fromDeg: number;
  toDeg: number;
  /** Degrees between two pegs. */
  pegDeg: number;
  /** The CSS cubic-bezier the disc turns on. */
  easing: readonly [number, number, number, number];
}

const MASTER_GAIN = 0.85;

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** A CSS cubic-bezier, as progress over time (0–1 → 0–1). */
function bezier([x1, y1, x2, y2]: readonly [number, number, number, number]) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const x = (s: number) => ((ax * s + bx) * s + cx) * s;
  const y = (s: number) => ((ay * s + by) * s + cy) * s;
  return (t: number): number => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    let lo = 0;
    let hi = 1;
    let s = t;
    for (let i = 0; i < 40; i += 1) {
      const value = x(s);
      if (Math.abs(value - t) < 1e-6) break;
      if (value < t) lo = s;
      else hi = s;
      s = (lo + hi) / 2;
    }
    return y(s);
  };
}

/** Frequency of a note, A4 = 440. */
function note(semitonesFromA4: number): number {
  return 440 * 2 ** (semitonesFromA4 / 12);
}

export class RouletteAudio {
  private context: AudioContext | null = null;
  private input: GainNode | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private tickBuffer: AudioBuffer | null = null;
  private live: AudioScheduledSourceNode[] = [];
  private volume = 0.7;

  /** From the spin button's tap: the only moment a browser lets audio start. */
  unlock(): void {
    if (this.context) {
      void this.context.resume().catch(() => {});
      return;
    }
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      const context = new Ctor();

      // input → compressor → master → speakers. Dry: no room, no tail.
      const input = context.createGain();
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.knee.value = 12;
      compressor.ratio.value = 3;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.18;
      const master = context.createGain();
      master.gain.value = MASTER_GAIN * this.volume;

      input.connect(compressor);
      compressor.connect(master);
      master.connect(context.destination);

      this.context = context;
      this.input = input;
      this.master = master;
      this.noise = this.makeNoise(context, 2);
      void this.loadTick(context);
    } catch {
      this.context = null;
    }
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(MASTER_GAIN * this.volume, this.context.currentTime, 0.05);
    }
  }

  /** Cuts everything still scheduled (the page was left mid-spin). */
  stop(): void {
    for (const source of this.live) {
      try {
        source.stop();
      } catch {
        /* already finished */
      }
    }
    this.live = [];
  }

  /** The whole turn of the disc, scheduled at once so it stays in step. */
  spin(plan: RouletteSpinPlan): void {
    const context = this.context;
    if (!context || !this.input || plan.durationMs <= 0) return;
    this.stop();
    const start = context.currentTime + 0.03;
    const seconds = plan.durationMs / 1000;
    const progress = bezier(plan.easing);
    const travel = plan.toDeg - plan.fromDeg;
    const angleAt = (t: number) => plan.fromDeg + travel * progress(t / seconds);

    this.release(start);

    // Where each peg passes the pointer: walk the curve finely and note every
    // crossing of a peg line, with the disc's speed there.
    const step = 0.002;
    const crossings: { at: number; speed: number }[] = [];
    let previous = angleAt(0);
    for (let t = step; t <= seconds + 1e-9; t += step) {
      const angle = angleAt(t);
      const a = Math.floor((previous - plan.pegDeg / 2) / plan.pegDeg);
      const b = Math.floor((angle - plan.pegDeg / 2) / plan.pegDeg);
      if (a !== b) crossings.push({ at: t, speed: Math.abs(angle - previous) / step });
      previous = angle;
    }
    const top = crossings.reduce((max, c) => Math.max(max, c.speed), 1);
    let last = -1;
    for (const crossing of crossings) {
      // Past ~45 a second the flapper is a blur; a real one turns into a rattle
      // there, and so does this, a little quieter.
      if (crossing.at - last < 0.022) continue;
      last = crossing.at;
      this.tick(start + crossing.at, crossing.speed / top);
    }


    // The disc comes to rest well before the curve formally ends — its tail is
    // a crawl nobody sees. The clunk goes where it actually stops moving.
    let restAt = seconds;
    for (let t = last > 0 ? last : 0; t < seconds; t += 0.01) {
      if (Math.abs(angleAt(t + 0.01) - angleAt(t)) / 0.01 / top < 0.012) {
        restAt = t;
        break;
      }
    }
    this.clunk(start + restAt + 0.04);
  }

  /** The prize told: rising bars for a win, two falling wooden notes for none. */
  prize(weight: RoulettePrizeWeight): void {
    const context = this.context;
    if (!context || !this.input) return;
    const at = context.currentTime + 0.05;
    if (weight === 'none') {
      this.woodNote(at, note(-2), 0.28);
      this.woodNote(at + 0.16, note(-7), 0.24);
      return;
    }
    // E major, from E6; a big prize climbs on to E7 and leaves a sparkle.
    const steps = weight === 'big' ? [7, 11, 14, 19] : [11, 14];
    steps.forEach((semis, index) => this.bar(at + index * 0.085, note(semis), weight === 'big' ? 0.3 : 0.26));
    if (weight === 'big') {
      this.bar(at + steps.length * 0.085 + 0.06, note(31), 0.09);
      this.bar(at + steps.length * 0.085 + 0.12, note(26), 0.07);
      this.thump(at, 70, 0.18, 0.08);
    }
  }

  //---------------------------------------------------------------------------
  // Voices
  //---------------------------------------------------------------------------

  private release(at: number): void {
    this.thump(at, 95, 0.55, 0.07);
    this.noiseBurst(at, { type: 'bandpass', frequency: 1800, q: 1.4, gain: 0.35, decay: 0.012 });
  }

  /** One peg under the flapper. `speed` is 0–1 of the fastest the disc turns. */
  private tick(at: number, speed: number): void {
    const context = this.context;
    const input = this.input;
    if (!context || !input) return;
    const loud = (0.32 + 0.5 * Math.sqrt(speed)) * rand(0.86, 1.08);

    // The recorded tick, a shade higher or lower each time.
    if (this.tickBuffer) {
      const source = context.createBufferSource();
      source.buffer = this.tickBuffer;
      source.playbackRate.value = rand(0.97, 1.03);
      const gain = context.createGain();
      gain.gain.value = loud;
      source.connect(gain);
      gain.connect(input);
      source.start(at);
      this.live.push(source);
      return;
    }

    // Centred: a click that wanders left and right reads as a reflection.
    const pan = input;

    // The strike.
    this.noiseBurst(at, { type: 'bandpass', frequency: rand(2800, 3600) + 900 * speed, q: 2.2, gain: loud, decay: 0.004 }, pan);

    // The flapper's ring: an inharmonic pair, damped fast.
    // Short: anything that rings past the next peg is heard as an echo.
    const f0 = rand(1150, 1300);
    this.partial(at, f0, loud * 0.42, 0.007, pan);
    this.partial(at, f0 * 2.83, loud * 0.16, 0.004, pan);

    // The peg's wood.
    this.partial(at, rand(205, 235), loud * 0.3, 0.01, pan);
  }

  private clunk(at: number): void {
    this.thump(at, 140, 0.32, 0.05);
    this.noiseBurst(at, { type: 'lowpass', frequency: 1200, q: 0.7, gain: 0.22, decay: 0.03 });
  }

  /** A struck metal bar (glockenspiel ratios), one clean voice. */
  private bar(at: number, frequency: number, level: number): void {
    const ratios = [1, 2.756, 5.404, 8.933];
    const amps = [1, 0.42, 0.2, 0.09];
    // One voice, short: the bar speaks and is gone. Two detuned copies, as
    // before, beat against each other and that beating reads as an echo.
    const decays = [0.22, 0.09, 0.045, 0.025];
    ratios.forEach((ratio, i) => this.partial(at, frequency * ratio, level * amps[i]!, decays[i]!, undefined, 0.0015));
    // A soft glow an octave under, what makes it a reward rather than a bell.
    this.partial(at, frequency / 2, level * 0.1, 0.1, undefined, 0.008);
  }

  /** A wooden bar, short and dry: the no-prize notes. */
  private woodNote(at: number, frequency: number, level: number): void {
    const ratios = [1, 3.93, 9.2];
    const amps = [1, 0.3, 0.08];
    const decays = [0.15, 0.05, 0.025];
    ratios.forEach((ratio, i) => this.partial(at, frequency * ratio, level * amps[i]!, decays[i]!));
    this.noiseBurst(at, { type: 'lowpass', frequency: 900, q: 0.7, gain: level * 0.35, decay: 0.01 });
  }

  /** A falling sine: the weight of the thing. */
  private thump(at: number, frequency: number, level: number, decay: number): void {
    const context = this.context;
    if (!context || !this.input) return;
    const osc = context.createOscillator();
    osc.frequency.setValueAtTime(frequency, at);
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.58, at + decay * 1.5);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(level, at + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + decay * 3);
    osc.connect(gain);
    gain.connect(this.input);
    osc.start(at);
    osc.stop(at + decay * 3 + 0.05);
    this.live.push(osc);
  }

  /** One damped sine partial. */
  private partial(
    at: number,
    frequency: number,
    level: number,
    decay: number,
    into?: AudioNode,
    attack = 0.0008,
  ): void {
    const context = this.context;
    if (!context || !this.input || frequency >= context.sampleRate / 2) return;
    const osc = context.createOscillator();
    osc.frequency.value = frequency;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), at + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay * 4);
    osc.connect(gain);
    gain.connect(into ?? this.input);
    osc.start(at);
    osc.stop(at + attack + decay * 4 + 0.05);
    this.live.push(osc);
  }

  private noiseBurst(
    at: number,
    shape: { type: BiquadFilterType; frequency: number; q: number; gain: number; decay: number },
    into?: AudioNode,
  ): void {
    const context = this.context;
    if (!context || !this.input || !this.noise) return;
    const source = context.createBufferSource();
    source.buffer = this.noise;
    const filter = context.createBiquadFilter();
    filter.type = shape.type;
    filter.frequency.value = Math.min(shape.frequency, context.sampleRate / 2 - 100);
    filter.Q.value = shape.q;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, shape.gain), at + 0.0005);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + shape.decay * 5);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(into ?? this.input);
    source.start(at, Math.random() * 1.5);
    source.stop(at + shape.decay * 5 + 0.02);
    this.live.push(source);
  }

  //---------------------------------------------------------------------------
  // Buffers
  //---------------------------------------------------------------------------

  private makeNoise(context: AudioContext, seconds: number): AudioBuffer {
    const buffer = context.createBuffer(1, Math.floor(context.sampleRate * seconds), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /**
   * Decodes roulette-tick.mp3 once. Vite inlines a file this small as a data:
   * URL, which the page's CSP (connect-src) does not let fetch() read, so that
   * case is decoded by hand; a real URL (the dev server) is fetched. If either
   * fails, the synthesised tick keeps playing.
   */
  private async loadTick(context: AudioContext): Promise<void> {
    try {
      let data: ArrayBuffer;
      if (tickUrl.startsWith('data:')) {
        const binary = atob(tickUrl.slice(tickUrl.indexOf(',') + 1));
        data = new ArrayBuffer(binary.length);
        const bytes = new Uint8Array(data);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      } else {
        const response = await fetch(tickUrl);
        if (!response.ok) return;
        data = await response.arrayBuffer();
      }
      this.tickBuffer = await context.decodeAudioData(data);
    } catch {
      /* the synthesised tick stays */
    }
  }

}

export const rouletteAudio = new RouletteAudio();
